import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'

/**
 * Hook for checking a single company-level permission
 * @param {string} permission - The permission to check (e.g., 'spaces.create')
 * @returns {boolean} - Whether the user has the permission
 *
 * @example
 * function CreateSpaceButton() {
 *   const canCreate = usePermission('spaces.create')
 *   return canCreate ? <button>Create Space</button> : null
 * }
 */
export function usePermission(permission) {
  const { can, permissionsLoaded } = useAuth()

  // Return false while permissions are loading to prevent flicker
  if (!permissionsLoaded) return false

  return can(permission)
}

/**
 * Hook for checking multiple company-level permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {Object} - Object mapping permission names to boolean values
 *
 * @example
 * function AdminPanel() {
 *   const perms = usePermissions(['spaces.create', 'users.invite', 'company.delete'])
 *   return (
 *     <div>
 *       {perms['spaces.create'] && <CreateSpaceButton />}
 *       {perms['users.invite'] && <InviteUserButton />}
 *       {perms['company.delete'] && <DeleteCompanyButton />}
 *     </div>
 *   )
 * }
 */
export function usePermissions(permissions) {
  const { can, permissionsLoaded } = useAuth()

  return permissions.reduce((acc, permission) => {
    // Return false while permissions are loading
    acc[permission] = permissionsLoaded ? can(permission) : false
    return acc
  }, {})
}

/**
 * Hook for checking if user can access/manage a specific space
 *
 * V2 Permissions Model:
 * - Admins: Can access all spaces automatically
 * - Managers: Must be in projects.access_control array
 * - Users/Workers: Must be in space_members table
 *
 * @param {string} projectId - The project/space ID
 * @returns {Object} - { canAccess: boolean, canManage: boolean, loading: boolean }
 *
 * @example
 * function SpaceSettings() {
 *   const { projectId } = useParams()
 *   const { canManage, loading } = useSpaceAccess(projectId)
 *
 *   if (loading) return <Spinner />
 *   if (!canManage) return <AccessDenied />
 *
 *   return <SettingsPanel />
 * }
 */
export function useSpaceAccess(projectId) {
  const { canAccessSpace, canManageSpace, permissionsLoaded } = useAuth()
  const [canAccess, setCanAccess] = useState(false)
  const [canManage, setCanManage] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    const checkAccess = async () => {
      if (!projectId || !permissionsLoaded) {
        if (mounted) {
          setLoading(!permissionsLoaded)
        }
        return
      }

      setLoading(true)

      try {
        // Check both access and manage permissions in parallel
        const [accessResult, manageResult] = await Promise.all([
          canAccessSpace(projectId),
          canManageSpace(projectId)
        ])

        if (mounted) {
          setCanAccess(accessResult)
          setCanManage(manageResult)
        }
      } catch (error) {
        console.error('Error checking space access:', error)
        if (mounted) {
          setCanAccess(false)
          setCanManage(false)
        }
      } finally {
        if (mounted) {
          setLoading(false)
        }
      }
    }

    checkAccess()

    return () => {
      mounted = false
    }
  }, [projectId, canAccessSpace, canManageSpace, permissionsLoaded])

  return {
    canAccess,
    canManage,
    loading
  }
}

/**
 * Hook to check if user has admin-level access (wildcard permission)
 * @returns {Object} - { isCompanyAdmin: boolean }
 *
 * @example
 * function AdminBadge() {
 *   const { isCompanyAdmin } = useIsAdmin()
 *   return isCompanyAdmin ? <Badge>Admin</Badge> : null
 * }
 */
export function useIsAdmin() {
  const { permissions, permissionsLoaded } = useAuth()

  if (!permissionsLoaded) {
    return { isCompanyAdmin: false }
  }

  const isCompanyAdmin = permissions.company.includes('*')

  return {
    isCompanyAdmin
  }
}

/**
 * Hook to require a specific company permission or redirect
 * For space-specific access control, use useSpaceAccess instead
 *
 * @param {string} permission - The company permission to require
 * @param {Object} options - { fallbackPath?: string }
 * @returns {Object} - { hasAccess: boolean, loading: boolean }
 *
 * @example
 * function AdminOnlyPage() {
 *   const { hasAccess, loading } = useRequirePermission('company.edit', {
 *     fallbackPath: '/dashboard'
 *   })
 *
 *   if (loading) return <Spinner />
 *   if (!hasAccess) return null // Will redirect
 *
 *   return <AdminPanel />
 * }
 */
export function useRequirePermission(permission, options = {}) {
  const { fallbackPath = '/dashboard' } = options
  const { can, permissionsLoaded } = useAuth()
  const [hasRedirected, setHasRedirected] = useState(false)

  const hasAccess = permissionsLoaded ? can(permission) : false

  useEffect(() => {
    // Only redirect once permissions are loaded and user doesn't have access
    if (permissionsLoaded && !hasAccess && !hasRedirected) {
      setHasRedirected(true)
      window.location.href = fallbackPath
    }
  }, [permissionsLoaded, hasAccess, fallbackPath, hasRedirected])

  return {
    hasAccess,
    loading: !permissionsLoaded
  }
}

/**
 * Hook to require space management access or redirect
 * Combines company permission check with space access check
 *
 * @param {string} projectId - The project/space ID
 * @param {Object} options - { fallbackPath?: string }
 * @returns {Object} - { hasAccess: boolean, loading: boolean }
 *
 * @example
 * function SpaceSettingsPage() {
 *   const { projectId } = useParams()
 *   const { hasAccess, loading } = useRequireSpaceManagement(projectId, {
 *     fallbackPath: `/space/${projectId}`
 *   })
 *
 *   if (loading) return <Spinner />
 *   if (!hasAccess) return null // Will redirect
 *
 *   return <SpaceSettings />
 * }
 */
export function useRequireSpaceManagement(projectId, options = {}) {
  const { fallbackPath = '/dashboard' } = options
  const { canManage, loading: spaceLoading } = useSpaceAccess(projectId)
  const [hasRedirected, setHasRedirected] = useState(false)

  useEffect(() => {
    // Only redirect once loading is complete and user can't manage
    if (!spaceLoading && !canManage && !hasRedirected && projectId) {
      setHasRedirected(true)
      window.location.href = fallbackPath
    }
  }, [spaceLoading, canManage, fallbackPath, hasRedirected, projectId])

  return {
    hasAccess: canManage,
    loading: spaceLoading
  }
}

/**
 * Hook to get all company permissions for debugging
 * @returns {Object} - { company: string[], allPermissions: string[] }
 *
 * @example
 * function DebugPermissions() {
 *   const { company, allPermissions } = useAllPermissions()
 *   return (
 *     <div>
 *       <h3>Company: {company.join(', ')}</h3>
 *       <h3>All: {allPermissions.join(', ')}</h3>
 *     </div>
 *   )
 * }
 */
export function useAllPermissions() {
  const { permissions, permissionsLoaded } = useAuth()

  if (!permissionsLoaded) {
    return {
      company: [],
      allPermissions: [],
      loading: true
    }
  }

  return {
    company: permissions.company,
    allPermissions: [...permissions.company],
    loading: false
  }
}
